import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dashboardApi, type KitchenAnalyticsResponse } from "../../api/dashboardApi";
import { useAuth } from "../../auth/AuthContext";
import "../../styles/rational.css";

type RationalMachine = { id: string; name: string; provider?: string };

function toDateInputValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDuration(sec?: number | null) {
  if (sec == null || !Number.isFinite(sec)) return "—";
  const total = Math.max(0, Math.round(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

function formatNum(n?: number | null, decimals = 1) {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString("it-IT", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export default function KitchenAnalyticsPage() {
  const { token } = useAuth() as { token: string | null };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<KitchenAnalyticsResponse | null>(null);
  const [fromDate, setFromDate] = useState(() =>
    toDateInputValue(new Date(Date.now() - 7 * 24 * 3600 * 1000))
  );
  const [toDate, setToDate] = useState(() => toDateInputValue(new Date()));
  const [machineId, setMachineId] = useState<string>("");
  const [rationalMachines, setRationalMachines] = useState<RationalMachine[]>([]);

  useEffect(() => {
    dashboardApi
      .getMachinesByProvider(token, "RATIONAL")
      .then((machines: RationalMachine[]) => setRationalMachines(machines ?? []))
      .catch(() => setRationalMachines([]));
  }, [token]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    dashboardApi
      .getKitchenAnalytics(token, {
        from: fromDate,
        to: toDate,
        machineId: machineId || undefined,
      })
      .then((res: any) => {
        const payload = res?.data ?? res;
        setData(payload ?? null);
      })
      .catch((e) => {
        setError(e?.message ?? "Errore caricamento analytics cucina");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [token, fromDate, toDate, machineId]);

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex align-items-center gap-2 text-muted">
          <div className="spinner-border spinner-border-sm" />
          Caricamento analytics cucina…
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4 rational-analytics-page">
      <div className="rational-page-header mb-4">
        <div className="rational-page-header-content">
          <div className="rational-page-badge">Rational</div>
          <h1 className="rational-page-title">
            <i className="bi bi-bar-chart-line me-2" />
            Analytics Cucina
          </h1>
          <p className="rational-page-sub">
            KPI, consumi e manutenzione predittiva
          </p>
        </div>
        <Link to="/machines/RATIONAL" className="btn rational-btn-back">
          <i className="bi bi-arrow-left me-1" />
          Torna a Rational
        </Link>
      </div>

      <div className="row g-2 mb-4 rational-filters">
        <div className="col-auto">
          <label className="form-label small mb-0">Da</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label small mb-0">A</label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div className="col-auto">
          <label className="form-label small mb-0">Macchina</label>
          <select
            className="form-select form-select-sm"
            value={machineId}
            onChange={(e) => setMachineId(e.target.value)}
            style={{ minWidth: 180 }}
          >
            <option value="">Tutte le macchine Rational</option>
            {rationalMachines.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      {data && (
        <>
          {/* KPI Cards */}
          <div className="row g-3 mb-4">
            <div className="col-6 col-md-4 col-lg-2">
              <div className="rational-widget">
                <div className="rational-widget-title">Tempo medio cottura</div>
                <div className="rational-widget-value">
                  {formatDuration(data.avgCookingTimeSec)}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="rational-widget">
                <div className="rational-widget-title">Consumo totale</div>
                <div className="rational-widget-value">
                  {formatNum(data.totalEnergyKwh, 2)} kWh
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="rational-widget">
                <div className="rational-widget-title">Ore di utilizzo</div>
                <div className="rational-widget-value">
                  {formatNum(data.usageHours)} h
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="rational-widget">
                <div className="rational-widget-title">Cicli totali</div>
                <div className="rational-widget-value">
                  {formatNum(data.totalCycles, 0)}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="rational-widget">
                <div className="rational-widget-title">Cicli a manutenzione</div>
                <div className="rational-widget-value">
                  {formatNum(data.cyclesToNextMaintenance, 0)}
                </div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2">
              <div className="rational-widget">
                <div className="rational-widget-title">Temperatura media</div>
                <div className="rational-widget-value">
                  {formatNum(data.avgTemperature)} °C
                </div>
              </div>
            </div>
          </div>

          {/* Programmi più usati */}
          <div className="row mb-4">
            <div className="col-12 col-lg-6">
              <div className="rational-card">
                <div className="rational-card-header">
                  <i className="bi bi-collection" />
                  Uso programmi
                </div>
                <div className="card-body">
                  {data.programUsage.length === 0 ? (
                    <p className="text-muted mb-0">Nessun ciclo nel periodo</p>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {data.programUsage.map((p, i) => (
                        <li key={i} className="list-group-item d-flex justify-content-between">
                          <span>{p.programName}</span>
                          <strong>{p.count}</strong>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <p className="text-muted small">
            Periodo: {new Date(data.from).toLocaleDateString("it-IT")} –{" "}
            {new Date(data.to).toLocaleDateString("it-IT")}
          </p>
        </>
      )}
    </div>
  );
}
